import { useTranslation } from 'react-i18next';
import { Input } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { getLocalDateString } from '~/utils';
import {
  earliestPurchaseOrderDate,
  PURCHASE_ORDER_DATE_FIELDS,
  purchaseOrderDateError,
  type PurchaseOrderDateError,
  type PurchaseOrderDateField,
  type PurchaseOrderDates,
} from './purchaseOrderDates';

function errorKey(
  error: PurchaseOrderDateError,
  field: PurchaseOrderDateField
): string {
  return error === 'in_past'
    ? 'cart.purchase_order_dates.in_past'
    : `cart.purchase_order_dates.out_of_order.${field}`;
}

interface PurchaseOrderDatesSectionProps {
  dates: PurchaseOrderDates;
  onChange: (dates: PurchaseOrderDates) => void;
}

export function PurchaseOrderDatesSection({
  dates,
  onChange,
}: Readonly<PurchaseOrderDatesSectionProps>) {
  const { t } = useTranslation('cart');
  const today = getLocalDateString();

  return (
    <div data-testid="purchase-order-dates">
      <SectionPanel title={t('cart.purchase_order_dates.title')}>
        <p className="text-sm text-neut-sec mb-4">
          {t('cart.purchase_order_dates.description')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PURCHASE_ORDER_DATE_FIELDS.map((field) => {
            const error = purchaseOrderDateError(field, dates, today);
            return (
              <Input
                key={field}
                name={field}
                type="date"
                required
                label={t(`cart.purchase_order_dates.${field}`)}
                min={earliestPurchaseOrderDate(field, dates, today)}
                value={dates[field]}
                error={error ? t(errorKey(error, field)) : undefined}
                onChange={(_, value) => onChange({ ...dates, [field]: value })}
              />
            );
          })}
        </div>
      </SectionPanel>
    </div>
  );
}
